import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import {
  FaBullhorn,
  FaDownload,
  FaPrint,
  FaRedo,
  FaImage,
} from 'react-icons/fa';
import SEO from '../components/SEO';
import { applyMobileInputHints } from '../utils/formEnhancements';

const THEMES = {
  sale: {
    name: 'Sale Spotlight',
    bg: '#fff7ed',
    accent: '#f97316',
    accentSoft: '#fed7aa',
    text: '#111827',
    muted: '#4b5563',
    badge: 'LIMITED OFFER',
  },
  boutique: {
    name: 'Boutique Glow',
    bg: '#fdf2f8',
    accent: '#ec4899',
    accentSoft: '#fbcfe8',
    text: '#1f2937',
    muted: '#4b5563',
    badge: 'NEW COLLECTION',
  },
  wellness: {
    name: 'Wellness Boost',
    bg: '#ecfeff',
    accent: '#0891b2',
    accentSoft: '#a5f3fc',
    text: '#0f172a',
    muted: '#334155',
    badge: 'HEALTH & CARE',
  },
  premium: {
    name: 'Premium Service',
    bg: '#f5f3ff',
    accent: '#7c3aed',
    accentSoft: '#ddd6fe',
    text: '#111827',
    muted: '#4b5563',
    badge: 'PREMIUM DEAL',
  },
};

const LAYOUTS = {
  instagramPost: { label: 'Instagram Post', width: 1080, height: 1080, orientation: 'square' },
  instagramStory: { label: 'Instagram Story', width: 1080, height: 1920, orientation: 'portrait' },
  youtube: { label: 'YouTube Banner', width: 2560, height: 1440, orientation: 'landscape' },
  facebook: { label: 'Facebook Promo', width: 1200, height: 1200, orientation: 'square' },
  custom: { label: 'Custom Size', width: 1200, height: 675, orientation: 'landscape' },
};

const DEFAULT_TEXT_PRESET = {
  sizes: {
    businessName: 58,
    tagline: 22,
    offerText: 82,
    cta: 28,
  },
  positions: {
    businessName: { x: 0.12, y: 0.19 },
    tagline: { x: 0.12, y: 0.32 },
    offerText: { x: 0.12, y: 0.46 },
    cta: { x: 0.18, y: 0.82 },
  },
};

const TEXT_PRESETS = {
  instagramPost: {
    sizes: {
      businessName: 46,
      tagline: 18,
      offerText: 54,
      cta: 20,
    },
    positions: {
      businessName: { x: 0.12, y: 0.235 },
      tagline: { x: 0.12, y: 0.345 },
      offerText: { x: 0.12, y: 0.5 },
      cta: { x: 0.18, y: 0.89 },
    },
  },
  facebook: {
    sizes: {
      businessName: 46,
      tagline: 18,
      offerText: 54,
      cta: 20,
    },
    positions: {
      businessName: { x: 0.12, y: 0.235 },
      tagline: { x: 0.12, y: 0.345 },
      offerText: { x: 0.12, y: 0.5 },
      cta: { x: 0.18, y: 0.89 },
    },
  },
  instagramStory: {
    sizes: {
      businessName: 66,
      tagline: 24,
      offerText: 96,
      cta: 30,
    },
    positions: {
      businessName: { x: 0.11, y: 0.17 },
      tagline: { x: 0.11, y: 0.28 },
      offerText: { x: 0.1, y: 0.42 },
      cta: { x: 0.16, y: 0.84 },
    },
  },
  youtube: {
    sizes: {
      businessName: 50,
      tagline: 20,
      offerText: 72,
      cta: 24,
    },
    positions: {
      businessName: { x: 0.11, y: 0.2 },
      tagline: { x: 0.11, y: 0.32 },
      offerText: { x: 0.11, y: 0.46 },
      cta: { x: 0.18, y: 0.8 },
    },
  },
  custom: {
    sizes: {
      businessName: 48,
      tagline: 20,
      offerText: 70,
      cta: 24,
    },
    positions: {
      businessName: { x: 0.11, y: 0.2 },
      tagline: { x: 0.11, y: 0.32 },
      offerText: { x: 0.11, y: 0.46 },
      cta: { x: 0.18, y: 0.8 },
    },
  },
};

const cloneTextPreset = (preset) => ({
  sizes: { ...preset.sizes },
  positions: {
    businessName: { ...preset.positions.businessName },
    tagline: { ...preset.positions.tagline },
    offerText: { ...preset.positions.offerText },
    cta: { ...preset.positions.cta },
  },
});

const autoFitSize = (text, preferred, min, targetChars) => {
  const content = (text || '').trim();
  if (!content) {
    return preferred;
  }

  const factor = clamp(targetChars / content.length, 0.6, 1);
  return Math.max(min, Math.round(preferred * factor));
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const hexToRgb = (hex) => {
  const value = hex.replace('#', '');
  const normalized = value.length === 3 ? value.split('').map((char) => char + char).join('') : value;
  const num = Number.parseInt(normalized, 16);

  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
};

const toRgba = (hex, alpha = 1) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const currentDate = () => new Date().toLocaleDateString('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const buildPosterTheme = (themeKey, customBg, customAccent, customText, customMuted) => {
  const base = THEMES[themeKey] || THEMES.sale;
  return {
    ...base,
    bg: customBg || base.bg,
    accent: customAccent || base.accent,
    text: customText || base.text,
    muted: customMuted || base.muted,
    accentSoft: toRgba(customAccent || base.accent, 0.18),
  };
};

const roundedRect = (ctx, x, y, width, height, radius) => {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

const fitCanvasFontSize = (ctx, text, initialSize, minSize, maxWidth, weight = 700, family = 'Arial') => {
  let size = initialSize;
  ctx.font = `${weight} ${size}px ${family}`;

  while (size > minSize && ctx.measureText(text).width > maxWidth) {
    size -= 1;
    ctx.font = `${weight} ${size}px ${family}`;
  }

  return size;
};

const drawPosterCanvas = ({ ctx, width, height, theme, businessName, tagline, offerText, subText, cta, phone, address, validity }) => {
  const softBg = toRgba(theme.accent, 0.12);

  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, theme.bg);
  gradient.addColorStop(1, softBg);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  const accentGlow = ctx.createRadialGradient(width * 0.8, height * 0.2, 20, width * 0.8, height * 0.2, width * 0.6);
  accentGlow.addColorStop(0, toRgba(theme.accent, 0.28));
  accentGlow.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = accentGlow;
  ctx.fillRect(0, 0, width, height);

  const headerH = height * 0.28;
  const panelX = width * 0.06;
  const panelY = height * 0.06;
  const panelW = width * 0.88;

  ctx.fillStyle = theme.accent;
  roundedRect(ctx, panelX, panelY, panelW, headerH, 28);
  ctx.fill();

  ctx.fillStyle = '#ffffff';
  const badgeText = theme.badge || 'LIMITED OFFER';
  const badgeX = panelX + width * 0.04;
  const badgeMaxWidth = panelW - width * 0.08;
  const badgeBaseSize = Math.max(18, width * 0.028);
  const badgeSize = fitCanvasFontSize(ctx, badgeText, badgeBaseSize, Math.max(12, badgeBaseSize * 0.65), badgeMaxWidth, 700);
  ctx.font = `700 ${badgeSize}px Arial`;
  ctx.fillText(badgeText, badgeX, panelY + headerH * 0.3);

  const businessTitle = businessName || 'Business Name';
  const businessTitleBaseSize = Math.max(34, width * 0.055);
  const businessTitleSize = fitCanvasFontSize(ctx, businessTitle, businessTitleBaseSize, Math.max(22, businessTitleBaseSize * 0.65), badgeMaxWidth, 700);
  ctx.font = `700 ${businessTitleSize}px Arial`;
  ctx.fillText(businessTitle, badgeX, panelY + headerH * 0.65);

  const headerLine = (tagline || 'Tagline') + ' • ' + currentDate();
  const headerBaseSize = Math.max(14, width * 0.022);
  const headerSize = fitCanvasFontSize(ctx, headerLine, headerBaseSize, Math.max(11, headerBaseSize * 0.72), badgeMaxWidth, 400);
  ctx.font = `400 ${headerSize}px Arial`;
  ctx.fillText(headerLine, badgeX, panelY + headerH * 0.84);

  const contentX = width * 0.08;
  const contentY = height * 0.4;
  const cardW = width * 0.84;
  const cardH = height * 0.38;

  ctx.fillStyle = 'rgba(255,255,255,0.78)';
  roundedRect(ctx, contentX, contentY, cardW, cardH, 28);
  ctx.fill();

  ctx.fillStyle = theme.text;
  const offerHeading = offerText || 'UP TO 40% OFF';
  const offerX = contentX + width * 0.04;
  const offerMaxWidth = cardW - width * 0.08;
  const offerBaseSize = Math.max(34, width * 0.07);
  const offerSize = fitCanvasFontSize(ctx, offerHeading, offerBaseSize, Math.max(24, offerBaseSize * 0.62), offerMaxWidth, 900);
  ctx.font = `900 ${offerSize}px Arial`;
  ctx.fillText(offerHeading, offerX, contentY + height * 0.14);

  ctx.fillStyle = theme.muted;
  const subBaseSize = Math.max(16, width * 0.024);
  const subPar = subText || 'On all salon and spa packages this month';
  const wrappedSub = subPar.length > 42 ? `${subPar.slice(0, 42)}...` : subPar;
  const subSize = fitCanvasFontSize(ctx, wrappedSub, subBaseSize, Math.max(13, subBaseSize * 0.72), offerMaxWidth, 600);
  ctx.font = `600 ${subSize}px Arial`;
  ctx.fillText(wrappedSub, offerX, contentY + height * 0.23);

  ctx.fillStyle = '#ffffff';
  const infoY = height * 0.76;
  const infoRowX = contentX + width * 0.02;
  const infoGap = width * 0.02;
  const infoW = (cardW - width * 0.04 - infoGap) / 2;
  const infoH = height * 0.11;

  roundedRect(ctx, infoRowX, infoY, infoW, infoH, 18);
  ctx.fillStyle = theme.accent;
  ctx.fill();

  ctx.fillStyle = '#ffffff';
  ctx.font = '700 ' + Math.max(12, width * 0.017) + 'px Arial';
  ctx.fillText('CALL', infoRowX + width * 0.03, infoY + infoH * 0.35);
  ctx.font = '700 ' + Math.max(15, width * 0.024) + 'px Arial';
  ctx.fillText((phone || '+91 98765 43210').slice(0, 16), infoRowX + width * 0.03, infoY + infoH * 0.7);

  const rightInfoX = infoRowX + infoW + infoGap;
  roundedRect(ctx, rightInfoX, infoY, infoW, infoH, 18);
  ctx.fillStyle = theme.accent;
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.font = '700 ' + Math.max(12, width * 0.017) + 'px Arial';
  ctx.fillText('VISIT', rightInfoX + width * 0.03, infoY + infoH * 0.35);
  const visitText = (address || 'Your location').slice(0, 24);
  const visitMaxWidth = infoW - width * 0.06;
  const visitBaseSize = Math.max(15, width * 0.024);
  const visitSize = fitCanvasFontSize(ctx, visitText, visitBaseSize, Math.max(12, visitBaseSize * 0.72), visitMaxWidth, 700);
  ctx.font = `700 ${visitSize}px Arial`;
  ctx.fillText(visitText, rightInfoX + width * 0.03, infoY + infoH * 0.7);

  const ctaY = height * 0.895;
  const ctaH = height * 0.08;
  const ctaX = width * 0.12;
  const ctaW = width * 0.76;
  roundedRect(ctx, ctaX, ctaY, ctaW, ctaH, 18);
  ctx.fillStyle = theme.accent;
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  const ctaText = (cta || 'Book Your Slot Today').toUpperCase();
  const ctaMaxWidth = ctaW - width * 0.1;
  const ctaBaseSize = Math.max(18, width * 0.03);
  const ctaSize = fitCanvasFontSize(ctx, ctaText, ctaBaseSize, Math.max(13, ctaBaseSize * 0.68), ctaMaxWidth, 800);
  ctx.font = `800 ${ctaSize}px Arial`;
  const ctaTextWidth = ctx.measureText(ctaText).width;
  const centeredCtaTextX = ctaX + (ctaW - ctaTextWidth) / 2;
  ctx.fillText(ctaText, centeredCtaTextX, ctaY + ctaH * 0.62);
};

const BusinessPosterGenerator = () => {
  const [themeKey, setThemeKey] = useState('sale');
  const [layoutKey, setLayoutKey] = useState('instagramPost');
  const [businessName, setBusinessName] = useState('Urban Glow Studio');
  const [tagline, setTagline] = useState('Beauty • Wellness • Style');
  const [offerText, setOfferText] = useState('UP TO 40% OFF');
  const [subText, setSubText] = useState('On all salon and spa packages this month');
  const [cta, setCta] = useState('Book Your Slot Today');
  const [phone, setPhone] = useState('+91 98765 43210');
  const [address, setAddress] = useState('MG Road, Bengaluru');
  const [validity, setValidity] = useState('Valid till 30 Nov 2026');
  const [canvasWidth, setCanvasWidth] = useState(LAYOUTS.instagramPost.width);
  const [canvasHeight, setCanvasHeight] = useState(LAYOUTS.instagramPost.height);
  const [customBg, setCustomBg] = useState(THEMES.sale.bg);
  const [customAccent, setCustomAccent] = useState(THEMES.sale.accent);
  const [customText, setCustomText] = useState(THEMES.sale.text);
  const [customMuted, setCustomMuted] = useState(THEMES.sale.muted);
  const [logoImage, setLogoImage] = useState('');
  const [logoPosition, setLogoPosition] = useState({ x: 28, y: 20 });
  const [previewScale, setPreviewScale] = useState(1);
  const [isCompactViewport, setIsCompactViewport] = useState(false);
  const [dragTextEnabled, setDragTextEnabled] = useState(false);
  const [textSizes, setTextSizes] = useState(() => cloneTextPreset(TEXT_PRESETS.instagramPost || DEFAULT_TEXT_PRESET).sizes);
  const [textPositions, setTextPositions] = useState(() => cloneTextPreset(TEXT_PRESETS.instagramPost || DEFAULT_TEXT_PRESET).positions);
  const dragRef = useRef(null);
  const dragTextRef = useRef(null);
  const previewSurfaceRef = useRef(null);
  const pageRef = useRef(null);

  const getSafeTextPosition = useCallback((key, x, y) => {
    const isSquareDefault = layoutKey === 'instagramPost' || layoutKey === 'facebook';
    if (!isSquareDefault) {
      return { x, y };
    }

    const xSafe = clamp(x, 0.08, key === 'cta' ? 0.78 : 0.82);
    if (key === 'businessName') {
      return { x: xSafe, y: clamp(y, 0.22, 0.28) };
    }
    if (key === 'tagline') {
      return { x: xSafe, y: clamp(y, 0.30, 0.37) };
    }
    if (key === 'offerText') {
      return { x: xSafe, y: clamp(y, 0.45, 0.54) };
    }
    if (key === 'cta') {
      return { x: xSafe, y: clamp(y, 0.87, 0.92) };
    }

    return { x: xSafe, y };
  }, [layoutKey]);

  const getTextDragBounds = useCallback((key) => {
    const isSquareDefault = layoutKey === 'instagramPost' || layoutKey === 'facebook';
    if (isSquareDefault) {
      if (key === 'businessName') {
        return { minX: 0.08, maxX: 0.82, minY: 0.22, maxY: 0.28 };
      }
      if (key === 'tagline') {
        return { minX: 0.08, maxX: 0.82, minY: 0.30, maxY: 0.37 };
      }
      if (key === 'offerText') {
        return { minX: 0.08, maxX: 0.82, minY: 0.45, maxY: 0.54 };
      }
      if (key === 'cta') {
        return { minX: 0.08, maxX: 0.78, minY: 0.87, maxY: 0.92 };
      }
    }

    if (key === 'cta') {
      return {
        minX: 0.05,
        maxX: 0.78,
        minY: 0.08,
        maxY: 0.9,
      };
    }

    return {
      minX: 0.05,
      maxX: 0.8,
      minY: 0.08,
      maxY: 0.82,
    };
  }, [layoutKey]);

  const theme = useMemo(
    () => buildPosterTheme(themeKey, customBg, customAccent, customText, customMuted),
    [themeKey, customBg, customAccent, customText, customMuted]
  );

  useEffect(() => {
    const updateViewportMode = () => {
      setIsCompactViewport(window.innerWidth <= 768);
    };

    updateViewportMode();
    window.addEventListener('resize', updateViewportMode);

    return () => {
      window.removeEventListener('resize', updateViewportMode);
    };
  }, []);

  useEffect(() => {
    applyMobileInputHints(pageRef.current);
  }, [canvasWidth, canvasHeight, textSizes, phone, address]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (dragTextRef.current) {
        const surface = document.getElementById('poster-preview-surface');
        if (!surface) {
          return;
        }

        const rect = surface.getBoundingClientRect();
        const { key, offsetX, offsetY } = dragTextRef.current;
        const bounds = getTextDragBounds(key);
        const nextX = clamp((event.clientX - rect.left - offsetX) / rect.width, bounds.minX, bounds.maxX);
        const nextY = clamp((event.clientY - rect.top - offsetY) / rect.height, bounds.minY, bounds.maxY);

        setTextPositions((prev) => ({
          ...prev,
          [key]: { x: nextX, y: nextY },
        }));
        return;
      }

      if (!dragRef.current) {
        return;
      }

      const surface = document.getElementById('poster-preview-surface');
      if (!surface) {
        return;
      }

      const rect = surface.getBoundingClientRect();
      const nextX = clamp(event.clientX - rect.left - dragRef.current.offsetX, 8, rect.width - 76);
      const nextY = clamp(event.clientY - rect.top - dragRef.current.offsetY, 8, rect.height - 76);
      setLogoPosition({ x: nextX, y: nextY });
    };

    const handlePointerUp = () => {
      dragRef.current = null;
      dragTextRef.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [getTextDragBounds]);

  useEffect(() => {
    const surface = previewSurfaceRef.current;
    if (!surface) {
      return undefined;
    }

    const updatePreviewScale = () => {
      const rect = surface.getBoundingClientRect();
      if (!rect.width || !canvasWidth) {
        return;
      }

      setPreviewScale(clamp(rect.width / canvasWidth, 0.2, 1));
    };

    updatePreviewScale();

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(updatePreviewScale);
      observer.observe(surface);
    }

    window.addEventListener('resize', updatePreviewScale);

    return () => {
      window.removeEventListener('resize', updatePreviewScale);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [canvasWidth]);

  const selectedLayout = LAYOUTS[layoutKey] || LAYOUTS.instagramPost;

  const applyPresetLayout = (key) => {
    const preset = LAYOUTS[key];
    const textPreset = cloneTextPreset(TEXT_PRESETS[key] || DEFAULT_TEXT_PRESET);

    setLayoutKey(key);
    setCanvasWidth(preset.width);
    setCanvasHeight(preset.height);
    setCustomBg(THEMES[key]?.bg || THEMES.sale.bg);
    setCustomAccent(THEMES[key]?.accent || THEMES.sale.accent);
    setCustomText(THEMES[key]?.text || THEMES.sale.text);
    setCustomMuted(THEMES[key]?.muted || THEMES.sale.muted);
    setThemeKey(key === 'custom' ? 'sale' : key);
    setTextSizes(textPreset.sizes);
    setTextPositions(textPreset.positions);
  };

  const resetForm = () => {
    const defaultPreset = cloneTextPreset(TEXT_PRESETS.instagramPost || DEFAULT_TEXT_PRESET);

    setThemeKey('sale');
    setLayoutKey('instagramPost');
    setBusinessName('Urban Glow Studio');
    setTagline('Beauty • Wellness • Style');
    setOfferText('UP TO 40% OFF');
    setSubText('On all salon and spa packages this month');
    setCta('Book Your Slot Today');
    setPhone('+91 98765 43210');
    setAddress('MG Road, Bengaluru');
    setValidity('Valid till 30 Nov 2026');
    setCanvasWidth(LAYOUTS.instagramPost.width);
    setCanvasHeight(LAYOUTS.instagramPost.height);
    setCustomBg(THEMES.sale.bg);
    setCustomAccent(THEMES.sale.accent);
    setCustomText(THEMES.sale.text);
    setCustomMuted(THEMES.sale.muted);
    setLogoImage('');
    setLogoPosition({ x: 28, y: 20 });
    setDragTextEnabled(false);
    setTextSizes(defaultPreset.sizes);
    setTextPositions(defaultPreset.positions);
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setLogoImage(String(reader.result || ''));
    };
    reader.readAsDataURL(file);
  };

  const handleLogoPointerDown = (event) => {
    const surface = document.getElementById('poster-preview-surface');
    if (!surface) {
      return;
    }

    event.preventDefault();
    const rect = surface.getBoundingClientRect();
    dragRef.current = {
      offsetX: event.clientX - rect.left - logoPosition.x,
      offsetY: event.clientY - rect.top - logoPosition.y,
    };
  };

  const handleTextPointerDown = (event, key) => {
    if (!dragTextEnabled) {
      return;
    }

    const surface = document.getElementById('poster-preview-surface');
    if (!surface) {
      return;
    }

    event.preventDefault();
    const rect = surface.getBoundingClientRect();
    const currentPosition = textPositions[key] || { x: 0.12, y: 0.2 };
    dragTextRef.current = {
      key,
      offsetX: event.clientX - rect.left - currentPosition.x * rect.width,
      offsetY: event.clientY - rect.top - currentPosition.y * rect.height,
    };
  };

  const updateTextSize = (key, value) => {
    setTextSizes((prev) => ({
      ...prev,
      [key]: clamp(Number(value) || 18, 12, 140),
    }));
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadImage = () => {
    const canvas = document.createElement('canvas');
    const scale = 2;
    const finalWidth = clamp(Number(canvasWidth) || 1080, 400, 4000);
    const finalHeight = clamp(Number(canvasHeight) || 1080, 400, 4000);

    canvas.width = finalWidth * scale;
    canvas.height = finalHeight * scale;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    ctx.scale(scale, scale);
    drawPosterCanvas({
      ctx,
      width: finalWidth,
      height: finalHeight,
      theme,
      businessName,
      tagline,
      offerText,
      subText,
      cta,
      phone,
      address,
      validity,
    });

    canvas.toBlob((blob) => {
      if (!blob) {
        return;
      }

      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${(businessName || 'business-poster').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
    }, 'image/png');
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: canvasWidth >= canvasHeight ? 'landscape' : 'portrait', unit: 'pt' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const bg = hexToRgb(theme.bg);
    const accent = hexToRgb(theme.accent);

    doc.setFillColor(bg.r, bg.g, bg.b);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');

    doc.setFillColor(accent.r, accent.g, accent.b);
    doc.roundedRect(32, 32, pageWidth - 64, 90, 18, 18, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(28);
    doc.text(businessName || 'Business Name', 54, 74);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.text((tagline || 'Tagline') + ' • ' + currentDate(), 54, 96);

    doc.setFillColor(255, 255, 255);
    doc.roundedRect(32, 150, pageWidth - 64, pageHeight - 210, 18, 18, 'F');

    doc.setTextColor(17, 24, 39);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(28);
    const offerLines = doc.splitTextToSize(offerText || 'SPECIAL OFFER', pageWidth - 150);
    doc.text(offerLines, 54, 210);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(16);
    const subLines = doc.splitTextToSize(subText || 'Big savings for your business customers', pageWidth - 140);
    doc.text(subLines, 54, 270);

    doc.setFillColor(accent.r, accent.g, accent.b);
    doc.roundedRect(54, 330, 150, 54, 16, 16, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('CALL NOW', 84, 363);
    doc.setFontSize(12);
    doc.text(phone || '+91 98765 43210', 84, 378);

    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Offer valid', 238, 346);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.text(validity || 'Valid till 30 Nov 2026', 238, 364);

    doc.setFont('helvetica', 'bold');
    doc.text('Visit us', 238, 392);
    doc.setFont('helvetica', 'normal');
    doc.text(address || 'Your business address', 238, 410);

    doc.setFillColor(accent.r, accent.g, accent.b);
    doc.roundedRect(54, pageHeight - 110, pageWidth - 140, 46, 16, 16, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text((cta || 'Book Your Slot Today').toUpperCase(), 128, pageHeight - 82);

    doc.save(`${(businessName || 'business-poster').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-poster.pdf`);
  };

  const previewTextBlocks = [
    {
      key: 'businessName',
      text: businessName || 'Urban Glow Studio',
      style: {
        left: `${getSafeTextPosition('businessName', textPositions.businessName.x, textPositions.businessName.y).x * 100}%`,
        top: `${getSafeTextPosition('businessName', textPositions.businessName.x, textPositions.businessName.y).y * 100}%`,
        fontSize: `${Math.max(12, Math.round(autoFitSize(businessName, textSizes.businessName, 28, 20) * previewScale))}px`,
        fontWeight: 900,
        letterSpacing: '-0.05em',
        color: theme.text,
        lineHeight: 1,
        maxWidth: '74%',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    {
      key: 'tagline',
      text: `${tagline || 'Beauty • Wellness • Style'} • ${validity || 'Valid till 30 Nov 2026'}`,
      style: {
        left: `${getSafeTextPosition('tagline', textPositions.tagline.x, textPositions.tagline.y).x * 100}%`,
        top: `${getSafeTextPosition('tagline', textPositions.tagline.x, textPositions.tagline.y).y * 100}%`,
        fontSize: `${Math.max(11, Math.round(autoFitSize(`${tagline || ''} ${validity || ''}`, textSizes.tagline, 13, 40) * previewScale))}px`,
        fontWeight: 600,
        color: theme.text,
        opacity: 0.9,
        lineHeight: 1.2,
        maxWidth: '76%',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    {
      key: 'offerText',
      text: offerText || 'UP TO 40% OFF',
      style: {
        left: `${getSafeTextPosition('offerText', textPositions.offerText.x, textPositions.offerText.y).x * 100}%`,
        top: `${getSafeTextPosition('offerText', textPositions.offerText.x, textPositions.offerText.y).y * 100}%`,
        fontSize: `${Math.max(16, Math.round(autoFitSize(offerText, textSizes.offerText, 26, 16) * previewScale))}px`,
        fontWeight: 900,
        letterSpacing: '-0.06em',
        color: theme.text,
        lineHeight: 0.94,
        maxWidth: '74%',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    {
      key: 'cta',
      text: cta || 'Book Your Slot Today',
      style: {
        left: `${getSafeTextPosition('cta', textPositions.cta.x, textPositions.cta.y).x * 100}%`,
        top: `${getSafeTextPosition('cta', textPositions.cta.x, textPositions.cta.y).y * 100}%`,
        fontSize: `${Math.max(12, Math.round(autoFitSize(cta, textSizes.cta, 14, 24) * previewScale))}px`,
        fontWeight: 900,
        letterSpacing: '0.06em',
        color: '#ffffff',
        textTransform: 'uppercase',
        background: theme.accent,
        borderRadius: `${Math.max(10, Math.round(18 * previewScale))}px`,
        padding: `${Math.max(10, Math.round(18 * previewScale))}px ${Math.max(12, Math.round(22 * previewScale))}px`,
        boxShadow: '0 18px 24px rgba(249, 115, 22, 0.25)',
      },
    },
  ];

  return (
    <div className="calculator-page fade-in" ref={pageRef}>
      <SEO
        title="Business Poster Generator"
        description="Free Business Poster Generator for deals, sales, services, events, and local promotions. Customize text, colors, size, and download as PNG, PDF, or print instantly."
        keywords="business poster generator, promo poster maker, offer poster designer, shop advertisement poster, local business flyer maker, instagram poster, youtube poster template"
        canonicalPath="/business-poster"
        calculatorType="Business Poster Generator"
        calculatorDescription="Create eye-catching promotional posters for local businesses, sales offers, social media campaigns, events, and service launches."
        faqItems={[
          {
            question: 'What is the Business Poster Generator used for?',
            answer: 'It helps create promotional posters for offers, launches, services, seasonal campaigns, and social media marketing posts.',
          },
          {
            question: 'Can I customize the poster text, colors, and size?',
            answer: 'Yes. You can change the business details, accent colors, canvas size, and layout so it suits Instagram, YouTube, or custom promo needs.',
          },
          {
            question: 'Can I download the poster as an image?',
            answer: 'Yes. You can download the finished design as a high-quality PNG image or print it directly.',
          },
        ]}
      />

      <div className="calculator-header">
        <div className="calculator-title-wrap">
          <span className="calculator-icon accent-bg"><FaBullhorn /></span>
          <div>
            <h1>Business Poster Generator</h1>
            <p>Create Instagram, YouTube, and branded promotional posters in seconds.</p>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: isCompactViewport ? '16px' : '24px', alignItems: 'start' }}>
        <div style={{ background: '#fff', borderRadius: '22px', padding: '20px 18px', boxShadow: '0 12px 32px rgba(15, 23, 42, 0.08)', border: '1px solid rgba(148, 163, 184, 0.18)' }}>
          <div style={{ display: 'grid', gap: '14px' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>Poster Layout</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {Object.entries(LAYOUTS).map(([key, value]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => applyPresetLayout(key)}
                    style={{
                      border: key === layoutKey ? `2px solid ${theme.accent}` : '1px solid #e2e8f0',
                      background: key === layoutKey ? toRgba(theme.accent, 0.12) : '#f8fafc',
                      color: '#0f172a',
                      borderRadius: '12px',
                      padding: '8px 12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    {value.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>Poster Theme</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {Object.entries(THEMES).map(([key, value]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setThemeKey(key)}
                    style={{
                      border: key === themeKey ? `2px solid ${value.accent}` : '1px solid #e2e8f0',
                      background: key === themeKey ? value.accentSoft : '#f8fafc',
                      color: '#0f172a',
                      borderRadius: '12px',
                      padding: '8px 12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    {value.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>Business Name</label>
              <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} style={inputStyle} />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>Tagline</label>
              <input value={tagline} onChange={(e) => setTagline(e.target.value)} style={inputStyle} />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>Offer Heading</label>
              <input value={offerText} onChange={(e) => setOfferText(e.target.value)} style={inputStyle} />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>Offer Details</label>
              <textarea value={subText} onChange={(e) => setSubText(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>Call to Action</label>
              <input value={cta} onChange={(e) => setCta(e.target.value)} style={inputStyle} />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>Phone</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} style={inputStyle} />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>Address</label>
              <input value={address} onChange={(e) => setAddress(e.target.value)} style={inputStyle} />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>Validity</label>
              <input value={validity} onChange={(e) => setValidity(e.target.value)} style={inputStyle} />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>Text Editing</label>
              <button
                type="button"
                onClick={() => setDragTextEnabled((value) => !value)}
                style={{
                  ...ghostButtonStyle,
                  background: dragTextEnabled ? toRgba(theme.accent, 0.12) : '#fff',
                  borderColor: dragTextEnabled ? theme.accent : '#e2e8f0',
                  color: '#0f172a',
                }}
              >
                {dragTextEnabled ? 'Stop Drag Text' : 'Drag Text'}
              </button>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>Text Size</label>
              <div style={{ display: 'grid', gap: '10px' }}>
                {[
                  { key: 'businessName', label: 'Business Name' },
                  { key: 'tagline', label: 'Tagline' },
                  { key: 'offerText', label: 'Offer' },
                  { key: 'cta', label: 'CTA' },
                ].map(({ key, label }) => (
                  <label key={key} style={{ display: 'grid', gap: '6px', color: '#0f172a', fontWeight: 700 }}>
                    <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{label}</span>
                      <span style={{ color: '#475569', fontWeight: 600 }}>{textSizes[key]}px</span>
                    </span>
                    <input
                      type="range"
                      min="12"
                      max="140"
                      value={textSizes[key]}
                      onChange={(event) => updateTextSize(key, event.target.value)}
                      style={{ width: '100%', minHeight: '34px', touchAction: 'pan-y' }}
                    />
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>Logo Upload</label>
              <div style={{ display: 'grid', gap: '8px' }}>
                <input type="file" accept="image/*" onChange={handleLogoUpload} style={inputStyle} />
                {logoImage ? (
                  <button type="button" onClick={() => setLogoImage('')} style={ghostButtonStyle}>Remove Logo</button>
                ) : null}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>Custom Colors</label>
              <div style={{ display: 'grid', gridTemplateColumns: isCompactViewport ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
                <label style={colorFieldStyle}><span>Background</span><input type="color" value={customBg} onChange={(e) => setCustomBg(e.target.value)} /></label>
                <label style={colorFieldStyle}><span>Accent</span><input type="color" value={customAccent} onChange={(e) => setCustomAccent(e.target.value)} /></label>
                <label style={colorFieldStyle}><span>Text</span><input type="color" value={customText} onChange={(e) => setCustomText(e.target.value)} /></label>
                <label style={colorFieldStyle}><span>Muted</span><input type="color" value={customMuted} onChange={(e) => setCustomMuted(e.target.value)} /></label>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px', color: '#0f172a' }}>Custom Size</label>
              <div style={{ display: 'grid', gridTemplateColumns: isCompactViewport ? '1fr' : '1fr 1fr', gap: '10px' }}>
                <input
                  type="number"
                  min="400"
                  max="4000"
                  value={canvasWidth}
                  onChange={(e) => setCanvasWidth(clamp(Number(e.target.value) || 1080, 400, 4000))}
                  style={inputStyle}
                />
                <input
                  type="number"
                  min="400"
                  max="4000"
                  value={canvasHeight}
                  onChange={(e) => setCanvasHeight(clamp(Number(e.target.value) || 1080, 400, 4000))}
                  style={inputStyle}
                />
              </div>
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#475569' }}>
                {selectedLayout.label} • {canvasWidth} × {canvasHeight} px
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button type="button" onClick={handleDownloadImage} style={{ ...primaryButtonStyle, width: isCompactViewport ? '100%' : 'auto', justifyContent: 'center' }}>
                <FaImage style={{ marginRight: '8px' }} /> Download Image
              </button>
              <button type="button" onClick={handleDownloadPDF} style={{ ...secondaryButtonStyle, width: isCompactViewport ? '100%' : 'auto', justifyContent: 'center' }}>
                <FaDownload style={{ marginRight: '8px' }} /> PDF
              </button>
              <button type="button" onClick={handlePrint} style={{ ...secondaryButtonStyle, width: isCompactViewport ? '100%' : 'auto', justifyContent: 'center' }}>
                <FaPrint style={{ marginRight: '8px' }} /> Print
              </button>
              <button type="button" onClick={resetForm} style={{ ...ghostButtonStyle, width: isCompactViewport ? '100%' : 'auto', justifyContent: 'center' }}>
                <FaRedo style={{ marginRight: '8px' }} /> Reset
              </button>
            </div>
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: '22px', padding: '18px', boxShadow: '0 12px 32px rgba(15, 23, 42, 0.08)', border: '1px solid rgba(148, 163, 184, 0.18)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isCompactViewport ? 'flex-start' : 'center', flexDirection: isCompactViewport ? 'column' : 'row', gap: isCompactViewport ? '6px' : '0', marginBottom: '12px', color: '#334155' }}>
            <strong>{selectedLayout.label}</strong>
            <span>{canvasWidth} × {canvasHeight}</span>
          </div>

          <div
            id="poster-preview-surface"
            ref={previewSurfaceRef}
            style={{
              background: `radial-gradient(circle at top right, ${toRgba(theme.accent, 0.36)} 0%, rgba(255,255,255,0) 28%), linear-gradient(135deg, ${toRgba(theme.accent, 0.16)} 0%, ${theme.bg} 32%, ${theme.bg} 100%)`,
              color: theme.text,
              width: '100%',
              height: 'auto',
              maxWidth: '760px',
              margin: '0 auto',
              borderRadius: '22px',
              padding: isCompactViewport ? '12px 12px 10px' : '18px 18px 12px',
              boxShadow: '0 28px 55px rgba(15, 23, 42, 0.12)',
              border: `1px solid ${theme.accentSoft}`,
              position: 'relative',
              overflow: 'hidden',
              aspectRatio: `${canvasWidth} / ${canvasHeight}`,
            }}
          >
            {logoImage ? (
              <div
                onPointerDown={handleLogoPointerDown}
                style={{
                  position: 'absolute',
                  left: `${logoPosition.x}px`,
                  top: `${logoPosition.y}px`,
                  width: '64px',
                  height: '64px',
                  borderRadius: '16px',
                  background: 'rgba(255,255,255,0.9)',
                  boxShadow: '0 12px 22px rgba(15, 23, 42, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  cursor: 'grab',
                  touchAction: 'none',
                  zIndex: 3,
                }}
              >
                <img src={logoImage} alt="Brand logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            ) : null}

            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
              }}
            >
              <div style={{ position: 'absolute', left: '5%', right: '5%', top: '4.5%', height: '18%', background: `linear-gradient(135deg, ${theme.accent} 0%, ${toRgba(theme.accent, 0.78)} 100%)`, color: '#fff', borderRadius: '20px', padding: '18px 22px 22px', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.18), 0 18px 32px rgba(15, 23, 42, 0.08)', boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '14px', flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-block', fontWeight: 900, letterSpacing: '0.08em', fontSize: isCompactViewport ? '14px' : '18px', textTransform: 'uppercase', opacity: 0.95 }}>{theme.badge.split(' ')[0]}</span>
                  <span style={{ display: 'inline-block', fontWeight: 900, letterSpacing: '0.08em', fontSize: isCompactViewport ? '14px' : '18px', textTransform: 'uppercase', opacity: 0.9 }}>{theme.badge.includes(' ') ? theme.badge.split(' ').slice(1).join(' ') : 'OFFER'}</span>
                </div>
              </div>

              <div style={{ position: 'absolute', left: '8%', right: '8%', top: '29%', background: 'rgba(255,255,255,0.82)', borderRadius: '20px', padding: '18px 22px 20px', boxShadow: '0 12px 18px rgba(15, 23, 42, 0.05)', boxSizing: 'border-box' }} />

              <div style={{ position: 'absolute', right: '12%', top: '11%', width: '120px', height: '120px', background: toRgba(theme.accent, 0.14), borderRadius: '50%', filter: 'blur(8px)' }} />

              {previewTextBlocks.map(({ key, text, style }) => (
                <div
                  key={key}
                  onPointerDown={(event) => handleTextPointerDown(event, key)}
                  style={{
                    position: 'absolute',
                    left: style.left,
                    top: style.top,
                    zIndex: dragTextEnabled ? 8 : 4,
                    cursor: dragTextEnabled ? 'grab' : 'default',
                    userSelect: 'none',
                    whiteSpace: 'pre-wrap',
                    ...style,
                    color: key === 'cta' ? '#fff' : style.color,
                    background: key === 'cta' ? theme.accent : 'transparent',
                    borderRadius: key === 'cta' ? '18px' : '0',
                    padding: key === 'cta' ? '18px 22px' : '0',
                    boxShadow: key === 'cta' ? '0 18px 24px rgba(249, 115, 22, 0.25)' : 'none',
                    maxWidth: key === 'offerText' ? '500px' : 'none',
                    textShadow: key === 'offerText' ? '0 2px 10px rgba(255,255,255,0.35)' : 'none',
                    touchAction: dragTextEnabled ? 'none' : 'auto',
                  }}
                >
                  {text}
                </div>
              ))}

              <div style={{ position: 'absolute', left: '8%', right: '8%', top: '60%', background: 'rgba(255,255,255,0.82)', borderRadius: '18px', padding: '14px 22px 12px', boxShadow: '0 12px 18px rgba(15, 23, 42, 0.05)', boxSizing: 'border-box' }}>
                <div style={{ fontSize: 'clamp(16px, 2vw, 24px)', color: theme.muted, fontWeight: 500 }}>
                  {subText || 'On all salon and spa packages this month'}
                </div>
              </div>

              <div style={{ position: 'absolute', left: '8%', right: '8%', top: '73%', display: 'grid', gridTemplateColumns: isCompactViewport ? '1fr' : '1fr 1fr', gap: '10px' }}>
                <div style={{ background: theme.accent, borderRadius: '16px', padding: '16px 18px', color: '#fff', fontWeight: 800, letterSpacing: '0.06em', minHeight: '70px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '12px', opacity: 0.9, textTransform: 'uppercase' }}>Call</span>
                  <span style={{ fontSize: 'clamp(15px, 2vw, 22px)', letterSpacing: '-0.04em', marginTop: '4px' }}>{phone || '+91 98765 43210'}</span>
                </div>
                <div style={{ background: theme.accent, borderRadius: '16px', padding: '16px 18px', color: '#fff', fontWeight: 800, letterSpacing: '0.06em', minHeight: '70px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '12px', opacity: 0.9, textTransform: 'uppercase' }}>Visit</span>
                  <span style={{ fontSize: 'clamp(14px, 1.7vw, 18px)', letterSpacing: '-0.03em', marginTop: '4px' }}>{address || 'MG Road, Bengaluru'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const inputStyle = {
  width: '100%',
  border: '1px solid #dfe7f1',
  borderRadius: '12px',
  padding: '12px 14px',
  fontSize: '15px',
  outline: 'none',
  background: '#f8fafc',
  boxSizing: 'border-box',
};

const colorFieldStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  border: '1px solid #dfe7f1',
  borderRadius: '12px',
  padding: '8px 10px',
  background: '#f8fafc',
  color: '#0f172a',
  fontWeight: 700,
};

const primaryButtonStyle = {
  border: 'none',
  background: 'linear-gradient(135deg, #f97316, #ef4444)',
  color: '#fff',
  borderRadius: '12px',
  padding: '12px 16px',
  fontWeight: 800,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
};

const secondaryButtonStyle = {
  border: '1px solid #dbeafe',
  background: '#eff6ff',
  color: '#0f172a',
  borderRadius: '12px',
  padding: '12px 16px',
  fontWeight: 800,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
};

const ghostButtonStyle = {
  border: '1px solid #e2e8f0',
  background: '#fff',
  color: '#0f172a',
  borderRadius: '12px',
  padding: '12px 16px',
  fontWeight: 800,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
};

export default BusinessPosterGenerator;
